import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Progress, Button, Typography, Space, Tag, Empty, Statistic, Divider, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined, InfoCircleOutlined, BulbOutlined, ArrowUpOutlined } from '@ant-design/icons';
import dayjs from '../../../dayjs';
import OTRInsightCard from './OTRInsightCard';
import { analyzeDailyPerformanceWithRuleset } from '../utils/OTRKPIAIRuleset';

const { Text, Title, Paragraph } = Typography;

/**
 * OTRDailyOverviewTab component displays a date carousel with radial pie charts
 * showing OTR performance by date, using the same calculation logic as the Performance Overview tab.
 */
const OTRDailyOverviewTab = ({ data, selectedStopTypes, thresholds, onStopTypeChange }) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [visibleDates, setVisibleDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [datePerformance, setDatePerformance] = useState({});
  const [dailyInsights, setDailyInsights] = useState(null);
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);

  // Generate dates for the current month
  useEffect(() => {
    const dates = [];
    const startDate = currentMonth.startOf('month');
    const daysInMonth = currentMonth.daysInMonth();
    
    for (let i = 0; i < daysInMonth; i++) {
      dates.push(startDate.clone().add(i, 'day'));
    }
    
    setVisibleDates(dates);
  }, [currentMonth]);
  
  // Calculate performance by date using the data
  useEffect(() => {
    if (!data || data.length === 0) {
      setDatePerformance({});
      return;
    }
    
    // Group data by date
    calculatePerformanceByDate(data);
  }, [data, selectedStopTypes, thresholds]);
  
  // Find and select the most recent date with data
  useEffect(() => {
    if (!initialSelectionDone && Object.keys(datePerformance).length > 0) {
      // Get all dates with data
      const datesWithData = Object.keys(datePerformance)
        .filter(dateStr => {
          const perfData = datePerformance[dateStr];
          // Check that we have actual data in this date entry
          return perfData && (
            (perfData.totalCount && perfData.totalCount > 0) || 
            (perfData.firstStops && perfData.firstStops.total && perfData.firstStops.total > 0) ||
            (perfData.midStops && perfData.midStops.total && perfData.midStops.total > 0) ||
            (perfData.lastStops && perfData.lastStops.total && perfData.lastStops.total > 0)
          );
        })
        .map(dateStr => {
          // Handle different date formats
          if (dateStr.includes('-')) {
            return dayjs(dateStr);
          } else {
            return dayjs(dateStr, 'YYYYMMDD');
          }
        })
        .sort((a, b) => b.valueOf() - a.valueOf()); // Sort by date, newest first
      
      console.log('Found dates with data:', datesWithData.map(d => d.format('YYYY-MM-DD')));
      
      // If we have dates with data, select the most recent one
      if (datesWithData.length > 0) {
        const mostRecentDate = datesWithData[0];
        console.log('Selecting most recent date with data:', mostRecentDate.format('YYYY-MM-DD'));
        
        setSelectedDate(mostRecentDate);
        
        // If the most recent date is not in the current month view, update the month
        if (mostRecentDate.month() !== currentMonth.month() || 
            mostRecentDate.year() !== currentMonth.year()) {
          setCurrentMonth(mostRecentDate);
        }
        
        // Generate AI insights for this date
        const dateStr = mostRecentDate.format('YYYYMMDD');
        const dateData = data.filter(record => 
          String(record.DATE_ID) === dateStr ||
          record.dateStr === dateStr
        );
        
        const insights = analyzeDailyPerformanceWithRuleset(dateStr, dateData);
        setDailyInsights(insights);
      }
      
      setInitialSelectionDone(true);
    }
  }, [datePerformance, data, currentMonth, initialSelectionDone]);
  
  // Calculate performance for each date
  const calculatePerformanceByDate = (rawData) => {
    if (!rawData || rawData.length === 0) {
      setDatePerformance({});
      return;
    }
    
    // First group all records by date
    const recordsByDate = {};
    
    // Group all records by date
    rawData.forEach(record => {
      const dateId = record.DATE_ID;
      if (!dateId) return;
      
      // Use String(dateId) directly instead of formatted date to match OTRDashboard.js
      const dateStr = String(dateId);
      
      if (!recordsByDate[dateStr]) {
        recordsByDate[dateStr] = [];
      }
      
      recordsByDate[dateStr].push(record);
    });
    
    // Calculate performance for each date
    const performanceByDate = {};
    
    // Process each date's data separately
    Object.keys(recordsByDate).forEach(dateStr => {
      const dateRecords = recordsByDate[dateStr];
      
      // Calculate performance for each date
      if (!performanceByDate[dateStr]) {
        performanceByDate[dateStr] = {
          onTimeStops: 0,
          earlyStops: 0,
          lateStops: 0,
          totalStops: 0,
          firstStops: { onTime: 0, early: 0, late: 0, total: 0 },
          midStops: { onTime: 0, early: 0, late: 0, total: 0 },
          lastStops: { onTime: 0, early: 0, late: 0, total: 0 },
          // Add tracking for variance type
          departureVarianceCount: 0,
          arrivalVarianceCount: 0,
          noVarianceCount: 0
        };
      }
      
      // Process all records for statistics regardless of stop type
      dateRecords.forEach(record => {
        // Calculate variance - exact same logic as in OTRDashboard
        let variance = null;
        let varianceType = 'none';
        
        if (record.DEPARTURE_TIME_VARIANCE !== undefined && record.DEPARTURE_TIME_VARIANCE !== null) {
          variance = record.DEPARTURE_TIME_VARIANCE;
          varianceType = 'departure';
          performanceByDate[dateStr].departureVarianceCount++;
        } else if (record.A_DEP !== undefined && record.A_DEP !== null && 
                 record.S_DEP !== undefined && record.S_DEP !== null) {
          variance = record.A_DEP - record.S_DEP;
          varianceType = 'departure';
          performanceByDate[dateStr].departureVarianceCount++;
        } else if (record.ARRIVAL_TIME_VARIANCE !== undefined && record.ARRIVAL_TIME_VARIANCE !== null) {
          variance = record.ARRIVAL_TIME_VARIANCE;
          varianceType = 'arrival';
          performanceByDate[dateStr].arrivalVarianceCount++;
        } else {
          performanceByDate[dateStr].noVarianceCount++;
        }
        
        if (variance !== null) {
          // Categorize by stop type - we'll store all stats regardless of current filter
          if (record.TSN_FIRST_LAST_IND === 'F') {
            performanceByDate[dateStr].firstStops.total++;
            if (variance >= thresholds.early && variance <= thresholds.late) {
              performanceByDate[dateStr].firstStops.onTime++;
            } else if (variance < thresholds.early) {
              performanceByDate[dateStr].firstStops.early++;
            } else {
              performanceByDate[dateStr].firstStops.late++;
            }
          } else if (record.TSN_FIRST_LAST_IND === 'M') {
            performanceByDate[dateStr].midStops.total++;
            if (variance >= thresholds.early && variance <= thresholds.late) {
              performanceByDate[dateStr].midStops.onTime++;
            } else if (variance < thresholds.early) {
              performanceByDate[dateStr].midStops.early++;
            } else {
              performanceByDate[dateStr].midStops.late++;
            }
          } else if (record.TSN_FIRST_LAST_IND === 'L') {
            performanceByDate[dateStr].lastStops.total++;
            if (variance >= thresholds.early && variance <= thresholds.late) {
              performanceByDate[dateStr].lastStops.onTime++;
            } else if (variance < thresholds.early) {
              performanceByDate[dateStr].lastStops.early++;
            } else {
              performanceByDate[dateStr].lastStops.late++;
            }
          }
        }
      });
      
      // Now calculate the overall stats based on selectedStopTypes for this date
      // This matches exactly how OTRDashboard.js does it
      const filteredRecords = dateRecords.filter(record => {
        // Check if the record has a stop type that matches our filters
        if (selectedStopTypes.includes('ALL')) return true;
        return selectedStopTypes.includes(record.TSN_FIRST_LAST_IND);
      });
      
      // Count stops that have variance data
      const stopsWithVariance = filteredRecords.filter(record => {
        // Check if record has variance data
        if (record.DEPARTURE_TIME_VARIANCE !== undefined && record.DEPARTURE_TIME_VARIANCE !== null) return true;
        if (record.A_DEP !== undefined && record.A_DEP !== null && 
            record.S_DEP !== undefined && record.S_DEP !== null) return true;
        if (record.ARRIVAL_TIME_VARIANCE !== undefined && record.ARRIVAL_TIME_VARIANCE !== null) return true;
        return false;
      });
      
      // Calculate on-time percent using all filtered records (treating no-variance as not on-time)
      let onTimeCount = 0;
      let totalCount = filteredRecords.length; // Use all filtered records as denominator
      
      // Track for debugging
      let departureVarianceCount = 0;
      let arrivalVarianceCount = 0;
      let noVarianceCount = 0;
      
      // Count on-time departures
      stopsWithVariance.forEach(record => {
        // First look for departure variance
        let variance = null;
        if (record.DEPARTURE_TIME_VARIANCE !== undefined && record.DEPARTURE_TIME_VARIANCE !== null) {
          variance = record.DEPARTURE_TIME_VARIANCE;
          departureVarianceCount++;
        } 
        // Next try to calculate from actual/scheduled departure
        else if (record.A_DEP !== undefined && record.A_DEP !== null && 
          record.S_DEP !== undefined && record.S_DEP !== null) {
          variance = record.A_DEP - record.S_DEP;
          departureVarianceCount++;
        }
        // Fallback to arrival variance only if departure isn't available
        else if (record.ARRIVAL_TIME_VARIANCE !== undefined && record.ARRIVAL_TIME_VARIANCE !== null) {
          variance = record.ARRIVAL_TIME_VARIANCE;
          arrivalVarianceCount++;
        }
        
        // Check if on time using thresholds
        if (variance !== null && variance >= thresholds.early && variance <= thresholds.late) {
          onTimeCount++;
        }
      });
      
      // Count records with no variance data
      noVarianceCount = totalCount - stopsWithVariance.length;
      
      // Calculate OTR percentage using all records (treating no-variance as not on-time)
      const onTimePercent = Math.round((onTimeCount / totalCount) * 100);
      
      // Store in state
      performanceByDate[dateStr] = {
        dateStr,
        onTimePercent,
        onTimeCount,
        totalCount,
        departureVarianceCount,
        arrivalVarianceCount,
        noVarianceCount,
        firstStops: {
          total: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'F').length,
          onTime: filteredRecords.filter(r => {
            if (r.TSN_FIRST_LAST_IND !== 'F') return false;
            
            let variance = null;
            if (r.DEPARTURE_TIME_VARIANCE !== undefined && r.DEPARTURE_TIME_VARIANCE !== null) {
              variance = r.DEPARTURE_TIME_VARIANCE;
            } else if (r.A_DEP !== undefined && r.A_DEP !== null && 
                      r.S_DEP !== undefined && r.S_DEP !== null) {
              variance = r.A_DEP - r.S_DEP;
            } else if (r.ARRIVAL_TIME_VARIANCE !== undefined && r.ARRIVAL_TIME_VARIANCE !== null) {
              variance = r.ARRIVAL_TIME_VARIANCE;
            }
            
            return variance !== null && variance >= thresholds.early && variance <= thresholds.late;
          }).length
        },
        midStops: {
          total: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'M').length,
          onTime: filteredRecords.filter(r => {
            if (r.TSN_FIRST_LAST_IND !== 'M') return false;
            
            let variance = null;
            if (r.DEPARTURE_TIME_VARIANCE !== undefined && r.DEPARTURE_TIME_VARIANCE !== null) {
              variance = r.DEPARTURE_TIME_VARIANCE;
            } else if (r.A_DEP !== undefined && r.A_DEP !== null && 
                      r.S_DEP !== undefined && r.S_DEP !== null) {
              variance = r.A_DEP - r.S_DEP;
            } else if (r.ARRIVAL_TIME_VARIANCE !== undefined && r.ARRIVAL_TIME_VARIANCE !== null) {
              variance = r.ARRIVAL_TIME_VARIANCE;
            }
            
            return variance !== null && variance >= thresholds.early && variance <= thresholds.late;
          }).length
        },
        lastStops: {
          total: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'L').length,
          onTime: filteredRecords.filter(r => {
            if (r.TSN_FIRST_LAST_IND !== 'L') return false;
            
            let variance = null;
            if (r.DEPARTURE_TIME_VARIANCE !== undefined && r.DEPARTURE_TIME_VARIANCE !== null) {
              variance = r.DEPARTURE_TIME_VARIANCE;
            } else if (r.A_DEP !== undefined && r.A_DEP !== null && 
                      r.S_DEP !== undefined && r.S_DEP !== null) {
              variance = r.A_DEP - r.S_DEP;
            } else if (r.ARRIVAL_TIME_VARIANCE !== undefined && r.ARRIVAL_TIME_VARIANCE !== null) {
              variance = r.ARRIVAL_TIME_VARIANCE;
            }
            
            return variance !== null && variance >= thresholds.early && variance <= thresholds.late;
          }).length
        }
      };
    });
    
    setDatePerformance(performanceByDate);
  };
  
  // Calculate performance for a specific date based on selected stop types
  const calculatePerformanceForDate = (date) => {
    // First try to match the exact format used in datePerformance
    const formattedDate = date.format('YYYYMMDD');
    // Check all possible date formats to find a match
    const potentialKeys = [
      formattedDate,                // YYYYMMDD
      String(Number(formattedDate)),  // Remove leading zeros if present
      date.format('YYYY-MM-DD')      // YYYY-MM-DD
    ];
    
    let stats = null;
    // Try each format until we find a match
    for (const dateKey of potentialKeys) {
      if (datePerformance[dateKey]) {
        stats = datePerformance[dateKey];
        break;
      }
    }
    
    if (!stats) {
      return { percent: 0, total: 0 };
    }
    
    // If ALL is selected, use the overall stats
    if (selectedStopTypes.includes('ALL')) {
      return {
        percent: stats.onTimePercent,
        total: stats.totalCount,
        early: stats.earlyPercent,
        late: stats.latePercent,
        departureVarianceCount: stats.departureVarianceCount,
        arrivalVarianceCount: stats.arrivalVarianceCount,
        noVarianceCount: stats.noVarianceCount
      };
    }
    
    // Otherwise, combine the selected stop types
    let totalOnTime = 0;
    let totalEarly = 0;
    let totalLate = 0;
    let totalStops = 0;
    
    if (selectedStopTypes.includes('F')) {
      totalOnTime += stats.firstStops.onTime;
      totalEarly += stats.firstStops.early;
      totalLate += stats.firstStops.late;
      totalStops += stats.firstStops.total;
    }
    
    if (selectedStopTypes.includes('M')) {
      totalOnTime += stats.midStops.onTime;
      totalEarly += stats.midStops.early;
      totalLate += stats.midStops.late;
      totalStops += stats.midStops.total;
    }
    
    if (selectedStopTypes.includes('L')) {
      totalOnTime += stats.lastStops.onTime;
      totalEarly += stats.lastStops.early;
      totalLate += stats.lastStops.late;
      totalStops += stats.lastStops.total;
    }
    
    if (totalStops === 0) {
      return { 
        percent: 0, 
        total: 0, 
        early: 0, 
        late: 0,
        departureVarianceCount: stats.departureVarianceCount,
        arrivalVarianceCount: stats.arrivalVarianceCount,
        noVarianceCount: stats.noVarianceCount
      };
    }
    
    return {
      percent: Math.round((totalOnTime / totalStops) * 100),
      total: totalStops,
      early: Math.round((totalEarly / totalStops) * 100),
      late: Math.round((totalLate / totalStops) * 100),
      departureVarianceCount: stats.departureVarianceCount,
      arrivalVarianceCount: stats.arrivalVarianceCount,
      noVarianceCount: stats.noVarianceCount
    };
  };
  
  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentMonth(prev => prev.clone().subtract(1, 'month'));
  };
  
  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.clone().add(1, 'month'));
  };
  
  // Select a date
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    
    // Generate AI insights for the selected date
    if (data && data.length > 0) {
      const dateStr = date.format('YYYYMMDD');
      // Filter data for this specific date
      const dateData = data.filter(record => 
        String(record.DATE_ID) === dateStr ||
        record.dateStr === dateStr
      );
      
      // Get AI insights for the date's data
      const insights = analyzeDailyPerformanceWithRuleset(dateStr, dateData);
      setDailyInsights(insights);
    }
  };
  
  // Today button handler
  const handleTodayClick = () => {
    const today = dayjs();
    setSelectedDate(today);
    setCurrentMonth(today);
  };
  
  // Format date for display
  const formatDateDisplay = (date) => {
    return {
      day: date.format('ddd'),
      date: date.format('D'),
      month: date.format('MMM')
    };
  };
  
  // Check if a date is selected
  const isDateSelected = (date) => {
    // Get all possible formatted strings for this date
    const dateFormats = [
      date.format('YYYYMMDD'),                 // YYYYMMDD
      String(Number(date.format('YYYYMMDD'))), // Remove leading zeros
      date.format('YYYY-MM-DD')                // YYYY-MM-DD
    ];
    
    // Do the same for selected date
    const selectedDateFormats = [
      selectedDate.format('YYYYMMDD'),
      String(Number(selectedDate.format('YYYYMMDD'))),
      selectedDate.format('YYYY-MM-DD')
    ];
    
    // Check if any formats match
    return dateFormats.some(format => selectedDateFormats.includes(format));
  };
  
  // Check if a date is today
  const isToday = (date) => {
    // Get all possible formatted strings for this date
    const dateFormats = [
      date.format('YYYYMMDD'),                 // YYYYMMDD
      String(Number(date.format('YYYYMMDD'))), // Remove leading zeros
      date.format('YYYY-MM-DD')                // YYYY-MM-DD
    ];
    
    // Do the same for today
    const today = dayjs();
    const todayFormats = [
      today.format('YYYYMMDD'),
      String(Number(today.format('YYYYMMDD'))),
      today.format('YYYY-MM-DD')
    ];
    
    // Check if any formats match
    return dateFormats.some(format => todayFormats.includes(format));
  };
  
  // Helper function to get color based on percent value
  const getColorByPercent = (percent) => {
    // Update to match bar chart thresholds: 
    // Red < 75%, Orange 75-95%, Green ≥ 95%
    return percent >= 95 ? '#52c41a' : // green
           percent >= 75 ? '#faad14' : // orange/amber 
           '#f5222d';                  // red
  };
  
  // Calculate radius and thickness for the radial chart (for multiple rings)
  const calculateRadialConfig = (stopType) => {
    // When "ALL" is selected, show a single large ring
    if (selectedStopTypes.includes('ALL')) {
      return { strokeWidth: 12, radius: 80 };
    }
    
    // Count active stop types
    const activeTypes = selectedStopTypes.length;
    
    if (activeTypes === 1) {
      return { strokeWidth: 12, radius: 80 };
    }
    
    if (stopType === 'F') {
      return { strokeWidth: 10, radius: 80 };
    } else if (stopType === 'M') {
      return { strokeWidth: 10, radius: 60 };
    } else if (stopType === 'L') {
      return { strokeWidth: 10, radius: activeTypes === 3 ? 40 : 60 };
    }
    
    return { strokeWidth: 10, radius: 80 };
  };

  // Helper function to get performance data for a date using multiple format checks
  const getPerformanceDataForDate = (date) => {
    // Get all possible formatted strings for this date
    const dateFormats = [
      date.format('YYYYMMDD'),                 // YYYYMMDD
      String(Number(date.format('YYYYMMDD'))), // Remove leading zeros
      date.format('YYYY-MM-DD')                // YYYY-MM-DD
    ];
    
    // Try each format to find a match in datePerformance
    for (const format of dateFormats) {
      if (datePerformance[format]) {
        return datePerformance[format];
      }
    }
    
    return null;
  };

  // Add after the date carousel section
  const renderDailyInsights = () => {
    if (!dailyInsights || (!dailyInsights.insight && !dailyInsights.recommendation)) {
      return (
        <Card 
          title={
            <Tooltip title="AI-generated insights and recommendations for improving On-Time Running performance for this date">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <BulbOutlined style={{ color: '#faad14', marginRight: 8 }} />
                <span>AI Insights for {selectedDate.format('DD MMM YYYY')}</span>
              </div>
            </Tooltip>
          }
          style={{ marginTop: 16 }}
        >
          <Empty description="No significant performance issues detected for this date" />
        </Card>
      );
    }
    
    return (
      <Card 
        title={
          <Tooltip title="The most impactful AI-generated insight for improving On-Time Running performance on this date">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <BulbOutlined style={{ color: '#faad14', marginRight: 8 }} />
                <span>Highest Impact Insight for {selectedDate.format('DD MMM YYYY')}</span>
              </div>
              {dailyInsights.category && (
                <Tag color={dailyInsights.category === 'trip' ? 'blue' : dailyInsights.category === 'stop' ? 'purple' : 'orange'}>
                  {dailyInsights.category.charAt(0).toUpperCase() + dailyInsights.category.slice(1)}
                </Tag>
              )}
            </div>
          </Tooltip>
        }
        style={{ marginTop: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <InfoCircleOutlined style={{ marginRight: 8, marginTop: 4 }} />
              <div>
                <Text strong>Insight:</Text>
                <Paragraph style={{ marginTop: 4 }}>{dailyInsights.insight}</Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <InfoCircleOutlined style={{ marginRight: 8, marginTop: 4 }} />
              <div>
                <Text strong>Recommendation:</Text>
                <Paragraph style={{ marginTop: 4 }}>{dailyInsights.recommendation}</Paragraph>
              </div>
            </div>
          </Col>
          
          {dailyInsights.improvement > 0 && (
            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
              <Row justify="space-between" align="middle">
                <Col>
                  <Text>Estimated OTR Improvement:</Text>
                </Col>
                <Col>
                  <Statistic 
                    value={dailyInsights.improvement} 
                    suffix="%" 
                    valueStyle={{ 
                      color: dailyInsights.improvement >= 20 ? '#52c41a' : 
                             dailyInsights.improvement >= 10 ? '#faad14' : '#1890ff' 
                    }}
                    prefix={<ArrowUpOutlined />}
                  />
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  // Add a handler for stop type selection
  const handleStopTypeChange = (type, checked) => {
    let newSelection = [...selectedStopTypes];
    
    // Handle "All Stops" special case
    if (type === 'ALL') {
      // If "All Stops" was checked, make it the only selection
      if (checked) {
        newSelection = ['ALL'];
      } else {
        // If unchecking "All Stops", default to "First Stops"
        newSelection = ['F'];
      }
    } else {
      // Remove "ALL" if it's selected
      newSelection = newSelection.filter(t => t !== 'ALL');
      
      // Add or remove the specific type
      if (checked) {
        if (!newSelection.includes(type)) {
          newSelection.push(type);
        }
      } else {
        newSelection = newSelection.filter(t => t !== type);
      }
      
      // Ensure at least one type is selected
      if (newSelection.length === 0) {
        newSelection = ['F']; // Default to "First" if nothing selected
      }
    }
    
    // Pass the updated selection to the parent
    if (onStopTypeChange) {
      onStopTypeChange(newSelection);
    }
  };

  return (
    <div>
      {/* Month navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button 
          type="text" 
          icon={<LeftOutlined />} 
          onClick={handlePrevMonth} 
        />
        <Space>
          <Text strong style={{ fontSize: 16 }}>{currentMonth.format('MMMM YYYY')}</Text>
          <Button type="link" size="small" onClick={handleTodayClick}>Today</Button>
        </Space>
        <Button 
          type="text" 
          icon={<RightOutlined />} 
          onClick={handleNextMonth} 
        />
      </div>
      
      {/* Date carousel with radial charts */}
      <div style={{ 
        overflowX: 'auto', 
        whiteSpace: 'nowrap', 
        padding: '8px 0 24px 0',
        marginBottom: '20px' 
      }}>
        <div style={{ display: 'inline-flex' }}>
          {visibleDates.map((date, index) => {
            const dateStr = date.format('YYYYMMDD');
            const isDateToday = isToday(date);
            const isSelected = isDateSelected(date);
            const dateDisplay = formatDateDisplay(date);
            
            // Get performance data directly using the helper function
            const perfData = getPerformanceDataForDate(date);
            const performance = calculatePerformanceForDate(date);
            const hasData = perfData !== null && performance.total > 0;
            
            return (
              <div 
                key={index}
                onClick={() => handleDateSelect(date)}
                style={{ 
                  padding: '0 8px',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div 
                  style={{ 
                    border: isSelected ? '2px solid #1890ff' : 'none',
                    borderRadius: '8px',
                    padding: '8px 4px',
                    backgroundColor: isSelected ? 'rgba(24, 144, 255, 0.1)' : isDateToday ? 'rgba(240, 242, 245, 0.5)' : 'transparent'
                  }}
                >
                  <div style={{ color: isSelected ? '#1890ff' : '#666', fontSize: 12, marginBottom: 4 }}>
                    {dateDisplay.day}
                  </div>
                  
                  {/* Radial progress chart */}
                  <div style={{ margin: '8px 0', height: 80, width: 80, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {hasData ? (
                      <>
                        {/* Show appropriate rings based on selected stop types */}
                        {(selectedStopTypes.includes('ALL') || selectedStopTypes.includes('F')) && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadialConfig('F').radius) / 2, 
                              left: (80 - calculateRadialConfig('F').radius) / 2, 
                              width: calculateRadialConfig('F').radius, 
                              height: calculateRadialConfig('F').radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={selectedStopTypes.includes('ALL') ? 
                                performance.percent : 
                                perfData && perfData.firstStops && perfData.firstStops.total > 0 ?
                                Math.round((perfData.firstStops.onTime / perfData.firstStops.total) * 100) : 0}
                              format={() => ''}
                              strokeColor={getColorByPercent(selectedStopTypes.includes('ALL') ? 
                                performance.percent : 
                                perfData && perfData.firstStops && perfData.firstStops.total > 0 ?
                                Math.round((perfData.firstStops.onTime / perfData.firstStops.total) * 100) : 0)}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadialConfig('F').strokeWidth}
                              width={calculateRadialConfig('F').radius}
                            />
                          </div>
                        )}
                        
                        {selectedStopTypes.includes('M') && !selectedStopTypes.includes('ALL') && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadialConfig('M').radius) / 2, 
                              left: (80 - calculateRadialConfig('M').radius) / 2, 
                              width: calculateRadialConfig('M').radius, 
                              height: calculateRadialConfig('M').radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={perfData && perfData.midStops && perfData.midStops.total > 0 ?
                                Math.round((perfData.midStops.onTime / perfData.midStops.total) * 100) : 0}
                              format={() => ''}
                              strokeColor={getColorByPercent(perfData && perfData.midStops && perfData.midStops.total > 0 ?
                                Math.round((perfData.midStops.onTime / perfData.midStops.total) * 100) : 0)}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadialConfig('M').strokeWidth}
                              width={calculateRadialConfig('M').radius}
                            />
                          </div>
                        )}
                        
                        {selectedStopTypes.includes('L') && !selectedStopTypes.includes('ALL') && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadialConfig('L').radius) / 2, 
                              left: (80 - calculateRadialConfig('L').radius) / 2, 
                              width: calculateRadialConfig('L').radius, 
                              height: calculateRadialConfig('L').radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={perfData && perfData.lastStops && perfData.lastStops.total > 0 ?
                                Math.round((perfData.lastStops.onTime / perfData.lastStops.total) * 100) : 0}
                              format={() => ''}
                              strokeColor={getColorByPercent(perfData && perfData.lastStops && perfData.lastStops.total > 0 ?
                                Math.round((perfData.lastStops.onTime / perfData.lastStops.total) * 100) : 0)}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadialConfig('L').strokeWidth}
                              width={calculateRadialConfig('L').radius}
                            />
                          </div>
                        )}
                        
                        {/* Overall percentage in the center */}
                        <div style={{ 
                          position: 'absolute', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: 16, 
                          fontWeight: 'bold', 
                          color: '#000',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: 'inset 0 0 3px rgba(0,0,0,0.1)'
                        }}>
                          {performance.percent}%
                        </div>
                      </>
                    ) : (
                      <div style={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        border: '1px dashed #d9d9d9',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>No data</Text>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    fontSize: 14, 
                    fontWeight: isSelected || isDateToday ? 'bold' : 'normal',
                    color: isSelected ? '#1890ff' : isDateToday ? '#1890ff' : '#000'
                  }}>
                    {dateDisplay.date}
                  </div>
                  
                  <div style={{ fontSize: 12, color: '#999' }}>
                    {dateDisplay.month}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Selected date details */}
      {selectedDate && datePerformance[selectedDate.format('YYYYMMDD')] && (
        <Card 
          size="small"
          title={
            <Text style={{ fontSize: 16 }}>
              {selectedDate.format('dddd, MMMM D, YYYY')} Performance Details
              <span style={{ fontSize: 12, color: '#888', marginLeft: 10 }}>
                (Date ID: {selectedDate.format('YYYYMMDD')})
              </span>
            </Text>
          }
        >
          <Row gutter={24}>
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of stops that were on time (within the configured early/late thresholds) across all selected stop types">
                    <Space>
                      <span>Overall</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={calculatePerformanceForDate(selectedDate).percent}
                suffix="%"
                valueStyle={{ color: getColorByPercent(calculatePerformanceForDate(selectedDate).percent) }}
              />
              <Text type="secondary">{calculatePerformanceForDate(selectedDate).total} records</Text>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of first stops that were on time (within the configured early/late thresholds)">
                    <Space>
                      <span>First Stops</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={
                  datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total > 0 ? 
                  Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.onTime / 
                  datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total) * 100) : 0
                }
                suffix="%"
                valueStyle={{ 
                  color: getColorByPercent(
                    datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total > 0 ? 
                    Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.onTime / 
                    datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total) * 100) : 0
                  )
                }}
              />
              <Text type="secondary">{datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total || 0} records</Text>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of mid stops that were on time (within the configured early/late thresholds)">
                    <Space>
                      <span>Mid Stops</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={
                  datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total > 0 ? 
                  Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.onTime / 
                  datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total) * 100) : 0
                }
                suffix="%"
                valueStyle={{ 
                  color: getColorByPercent(
                    datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total > 0 ? 
                    Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.onTime / 
                    datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total) * 100) : 0
                  )
                }}
              />
              <Text type="secondary">{datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total || 0} records</Text>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of last stops that were on time (within the configured early/late thresholds)">
                    <Space>
                      <span>Last Stops</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={
                  datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total > 0 ? 
                  Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.onTime / 
                  datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total) * 100) : 0
                }
                suffix="%"
                valueStyle={{ 
                  color: getColorByPercent(
                    datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total > 0 ? 
                    Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.onTime / 
                    datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total) * 100) : 0
                  )
                }}
              />
              <Text type="secondary">{datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total || 0} records</Text>
            </Col>
          </Row>
        </Card>
      )}
      
      {/* AI Insights for selected date */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {renderDailyInsights()}
        </Col>
      </Row>
      
      {Object.keys(datePerformance).length === 0 && (
        <Empty 
          description="No performance data available for the selected period" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      )}
    </div>
  );
};

export default OTRDailyOverviewTab; 